/**
 * Shared rename affordance (TRA-947): a pencil button that opens a small
 * dialog and hands the trimmed name to the caller's mutation. Used for
 * benchmarks, eval groups and eval tasks — every rename is a label-only
 * change (the slug is the key and never moves), so one dialog fits all.
 *
 * The dialog closes on submit without waiting: callers apply the rename
 * optimistically (see useRenameBenchmark / useRenameEvalGroup /
 * useRenameEvalTask) and toast on failure, so keeping it open would only
 * show a spinner over an answer the cache already has.
 */

import { useState } from 'react'
import { Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTeam } from '@/contexts/TeamContext'

interface RenameDialogProps {
  /** Dialog title, e.g. "Rename benchmark". */
  title: string
  /** The immutable identifier, shown in the help text. */
  slug: string
  displayName: string
  maxLength: number
  onSave: (name: string) => void
}

export function RenameDialog({
  title,
  slug,
  displayName,
  maxLength,
  onSave,
}: RenameDialogProps): React.JSX.Element | null {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(displayName)
  // TRA-832: restricted demo teams 403 every PATCH server-side — hiding
  // the affordance here covers every current and future call site.
  const { isRestricted } = useTeam()
  if (isRestricted) return null

  const trimmed = name.trim()
  const canSubmit = trimmed.length > 0 && trimmed.length <= maxLength

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (!canSubmit) return
    setOpen(false)
    onSave(trimmed)
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        // Revealed by hovering the enclosing `group/rename` scope (the
        // title row / page header); opacity keeps it focusable, so keyboard
        // users still reach it and focus makes it visible.
        className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover/rename:opacity-100"
        aria-label={`Rename ${displayName}`}
        onClick={(event) => {
          // Some triggers live inside a card that is itself a <Link> — a
          // real DOM child of the anchor must stop the react-router handler
          // AND the browser's own anchor navigation.
          event.preventDefault()
          event.stopPropagation()
          setName(displayName)
          setOpen(true)
        }}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      {/*
        Radix portals the dialog to document.body, but React synthetic events
        bubble up the REACT tree — so a click on the overlay, the footer
        buttons or the X would still reach an enclosing card <Link> and
        navigate away mid-rename. One guard here covers the whole subtree
        (the overlay is a sibling of DialogContent, so guarding the content
        alone leaves click-outside-to-dismiss broken). No preventDefault:
        the portaled nodes are not DOM descendants of the anchor, so its
        default action never fires.
      */}
      <span
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rename-display-name">Name</Label>
                <Input
                  id="rename-display-name"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                  }}
                  maxLength={maxLength}
                  aria-describedby="rename-display-name-help"
                  autoComplete="off"
                  autoFocus
                />
                <p id="rename-display-name-help" className="text-xs text-muted-foreground">
                  Display name only. The URL keeps{' '}
                  <code className="font-mono">{slug}</code>.
                </p>
              </div>
              <DialogFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setOpen(false)
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </span>
    </>
  )
}
